import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useReassignRecording } from '@/api/mutations/useRecording';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useCreatedSuggestionSession } from '@/components/entity-pickers/hooks/useCreatedSuggestionSession';
import { toReassignPayload, type ReassignPayload } from '@/lib/successorEntityMapping';
import type { SuccessorUpdateData } from '../types';

interface UseReassignFlowArgs {
  recordingId: string | undefined;
  slug: string;
  setShowAssignInsteadModal: (open: boolean) => void;
}

/**
 * Handles the assign-someone-else flow: the context dialog's submission is
 * staged behind a confirm dialog (it warns that the recording's progress is
 * deleted), and on confirm the recording is reassigned and the user is routed
 * to My Tasks.
 *
 * A proposed (new) System/Team is created eagerly by the picker's suggest-new
 * dialog; the staged payload then carries its `{ type: 'suggestion', id }`
 * ref, so a confirm retry can never create a duplicate. The flow owns the
 * single created-suggestion ledger (`suggestionSession`, handed to the context
 * dialog as its `session`) and fires the guarded withdraw when either gate is
 * cancelled — the backend deletes only what is still pending and unreferenced,
 * so a suggestion another user adopted meanwhile survives.
 */
export function useReassignFlow({
  recordingId,
  slug,
  setShowAssignInsteadModal
}: UseReassignFlowArgs) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reassignRecording = useReassignRecording();

  const [showConfirmReassignDialog, setShowConfirmReassignDialog] = useState(false);
  const [pendingReassign, setPendingReassign] = useState<ReassignPayload | null>(null);
  // Single ledger for the whole flow: the context dialog tracks into it (it
  // keeps no ledger of its own when given a session), so each suggestion has
  // exactly one withdraw site. The session also feeds the confirm-dialog
  // preview, which resolves proposed names from the docs before any refetch.
  const suggestionSession = useCreatedSuggestionSession();

  const handleSuccessorsSubmit = useCallback(
    (data: SuccessorUpdateData) => {
      const payload = toReassignPayload(data);
      if (!payload) return;
      setPendingReassign(payload);
      setShowAssignInsteadModal(false);
      setShowConfirmReassignDialog(true);
    },
    [setShowAssignInsteadModal]
  );

  const handleConfirmReassign = useCallback(async () => {
    if (!recordingId || !pendingReassign) {
      toast.error(t('stepRecording.reassignMissingInfo'));
      return;
    }

    try {
      await reassignRecording.mutateAsync({
        recordingId,
        data: {
          rejectReason: pendingReassign.isUnclear ? 'unclear' : 're-assign',
          reAssigneeId: pendingReassign.reAssigneeId,
          reAssigneeModel: pendingReassign.reAssigneeModel,
          reAssigneeType: pendingReassign.reAssigneeType
        }
      });

      setShowConfirmReassignDialog(false);
      setPendingReassign(null);
      // The reassign persisted the confirmed suggestion's ref; the context
      // dialog already withdrew the ones it replaced on its own confirm.
      suggestionSession.clear();

      toast.success(t('stepRecording.reassignSuccess'));
      navigate({ to: '/$slug/my-tasks', params: { slug } });
    } catch (error) {
      console.error('Failed to reassign:', error);
      toastApiError(error, 'stepRecording.reassignError');
    }
  }, [recordingId, pendingReassign, reassignRecording, suggestionSession, navigate, slug, t]);

  const handleCancelReassign = useCallback(() => {
    setShowConfirmReassignDialog(false);
    setPendingReassign(null);
    // Cancelling the flow's last gate: withdraw everything created within the
    // flow, fire-and-forget — nothing persisted a ref (the reassign didn't
    // run), and the backend guard keeps anything adopted elsewhere meanwhile.
    suggestionSession.withdrawAll();
    // Reopen assignment modal so user can try again
    setShowAssignInsteadModal(true);
  }, [suggestionSession, setShowAssignInsteadModal]);

  return {
    showConfirmReassignDialog,
    pendingReassignData: pendingReassign,
    suggestionSession,
    handleSuccessorsSubmit,
    handleConfirmReassign,
    handleCancelReassign,
    reassignPending: reassignRecording.isPending
  };
}
